import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api';

function CompetitorTracker() {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [excludeWords, setExcludeWords] = useState('');
  const [keywordLimit, setKeywordLimit] = useState(100);
  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [sort, setSort] = useState('volume');
  const [error, setError] = useState('');
  const [lastFilteredOut, setLastFilteredOut] = useState(null);
  const fetchSeq = useRef(0);

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    if (selectedDomain) {
      fetchKeywords(selectedDomain.id, sort);
    }
  }, [selectedDomain?.id, sort]);

  const fetchDomains = async () => {
    try {
      const data = await apiFetch('/api/competitors');
      setDomains(data.domains || []);
      setConfigured(Boolean(data.configured));
      if (!selectedDomain && data.domains && data.domains.length > 0) {
        setSelectedDomain(data.domains[0]);
      }
    } catch (err) {
      console.error('Fetch competitor domains error:', err);
      setError(err.message);
    }
  };

  const fetchKeywords = async (domainId, sortBy = sort) => {
    const seq = ++fetchSeq.current;
    setLoadingKeywords(true);
    try {
      const data = await apiFetch(`/api/competitors/${domainId}/keywords?sort=${sortBy}`);
      // 丢弃过期响应：仅当仍是最新一次请求时才更新界面
      if (seq !== fetchSeq.current) return;
      setKeywords(data.keywords || []);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Fetch competitor keywords error:', err);
      setError(err.message);
    } finally {
      if (seq === fetchSeq.current) {
        setLoadingKeywords(false);
      }
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim() || loading) return;

    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/competitors/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain.trim(), keywordLimit, excludeWords: excludeWords.trim() })
      });
      // 后端有 upsert 语义（重复域名返回已有记录），用 id 去重避免列表重复
      setDomains(prev => {
        const exists = prev.some(d => d.id === data.domain.id);
        return exists ? prev.map(d => (d.id === data.domain.id ? data.domain : d)) : [data.domain, ...prev];
      });
      setSelectedDomain(data.domain);
      setNewDomain('');
      setExcludeWords('');
      if (data.filteredOut > 0) {
        setLastFilteredOut({ domain: data.domain.domain, count: data.filteredOut });
      }
    } catch (err) {
      console.error('Add competitor domain error:', err);
      setError(err.message);
      if (err.data?.domain) {
        setDomains(prev => {
          const exists = prev.some(d => d.id === err.data.domain.id);
          return exists ? prev : [err.data.domain, ...prev];
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (domain) => {
    setLoadingKeywords(true);
    setError('');
    try {
      const data = await apiFetch(`/api/competitors/${domain.id}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      setDomains(prev => prev.map(d => (d.id === data.domain.id ? data.domain : d)));
      // 同步更新当前选中竞品的信息（keyword_count / last_fetched 等）
      if (selectedDomain?.id === data.domain.id) {
        setSelectedDomain(data.domain);
        await fetchKeywords(data.domain.id, sort);
      }
      if (data.filteredOut > 0) {
        setLastFilteredOut({ domain: data.domain.domain, count: data.filteredOut });
      }
    } catch (err) {
      console.error('Refresh competitor error:', err);
      setError(err.message);
    } finally {
      setLoadingKeywords(false);
    }
  };

  const handleDelete = async (domain) => {
    try {
      await apiFetch(`/api/competitors/${domain.id}`, { method: 'DELETE' });
      // 用过滤后的新数组计算下一个选中项，避免选中已删除的竞品
      const remaining = domains.filter(d => d.id !== domain.id);
      setDomains(remaining);
      if (selectedDomain?.id === domain.id) {
        setSelectedDomain(remaining[0] || null);
        setKeywords([]);
      }
    } catch (err) {
      console.error('Delete competitor error:', err);
      setError(err.message);
    }
  };

  const handleExport = () => {
    if (!selectedDomain || keywords.length === 0 || loadingKeywords) return;

    const escapeCsvCell = (value) => {
      let text = value === null || value === undefined ? '' : String(value);
      // 防止用 Excel 打开 CSV 时将关键词解析为公式。
      if (/^[=+\-@]/.test(text)) text = `'${text}`;
      return `"${text.replace(/"/g, '""')}"`;
    };

    const headers = ['域名', '关键词', '排名', '搜索量', '难度', 'CPC($)', '预估流量', '排名页面'];
    const rows = keywords.map(keyword => [
      selectedDomain.domain,
      keyword.keyword,
      keyword.rank,
      keyword.search_volume,
      keyword.difficulty,
      keyword.cpc,
      keyword.etv,
      keyword.url
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(escapeCsvCell).join(','))
      .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeDomain = selectedDomain.domain.replace(/[^a-z0-9.-]/gi, '_');
    const date = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `${safeDomain}-keywords-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const formatNumber = (n) => (n ? Number(n).toLocaleString('en-US') : '0');
  const formatDate = (d) => (d ? new Date(d).toLocaleString('zh-CN') : '尚未抓取');

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <h2 style={{ fontSize: '18px', margin: '0 0 20px', color: '#1a1a1a' }}>🆚 竞品关键词分析</h2>

      {configured === false && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fff3e0', color: '#e65100', borderRadius: '6px', marginBottom: '20px', fontSize: '13px' }}>
          ⚠️ DataForSEO 未配置，请在 .env 中设置 DATAFORSEO_LOGIN 和 DATAFORSEO_PASSWORD
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fdecea', color: '#c62828', borderRadius: '6px', marginBottom: '20px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {lastFilteredOut && (
        <div style={{ padding: '12px 16px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '6px', marginBottom: '20px', fontSize: '13px' }}>
          ✅ {lastFilteredOut.domain} 已自动剔除 {lastFilteredOut.count} 个品牌关键词（如竞品名、品牌词）
        </div>
      )}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
          placeholder="输入竞争对手域名，如 competitor.com"
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
        <input
          type="text"
          value={excludeWords}
          onChange={(e) => setExcludeWords(e.target.value)}
          placeholder="额外排除词（逗号分隔，可选）"
          title="自动剔除竞品域名品牌词；如需额外排除其他品牌词，在此填写，逗号分隔"
          style={{
            flex: 1,
            minWidth: '160px',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
        <select
          value={keywordLimit}
          onChange={(e) => setKeywordLimit(Number(e.target.value))}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
        >
          <option value={100}>100 关键词</option>
          <option value={200}>200 关键词</option>
          <option value={500}>500 关键词</option>
          <option value={1000}>1000 关键词</option>
        </select>
        <button
          onClick={handleAddDomain}
          disabled={!newDomain.trim() || loading}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: '#0084ff',
            color: '#fff',
            cursor: newDomain.trim() && !loading ? 'pointer' : 'not-allowed',
            fontSize: '14px'
          }}
        >
          {loading ? '⏳ 抓取中...' : '+ 添加竞品'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
        <div>
          <h4 style={{ margin: '0 0 15px', fontSize: '14px', color: '#666' }}>竞品列表</h4>
          {domains.length === 0 && (
            <p style={{ color: '#999', fontSize: '13px' }}>暂无竞品，请先添加</p>
          )}
          {domains.map(d => (
            <div
              key={d.id}
              onClick={() => setSelectedDomain(d)}
              style={{
                padding: '12px',
                border: selectedDomain?.id === d.id ? '2px solid #0084ff' : '1px solid #eee',
                borderRadius: '6px',
                marginBottom: '10px',
                cursor: 'pointer',
                backgroundColor: selectedDomain?.id === d.id ? '#e3f2fd' : '#fff'
              }}
            >
              <div style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>{d.domain}</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {d.keyword_count || 0} 个关键词 · 总搜索量 {formatNumber(d.total_volume)}
              </div>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                上次抓取：{formatDate(d.last_fetched)}
              </div>
              {d.exclude_words ? (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  排除词：{d.exclude_words}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  自动剔除品牌词：{d.domain.replace(/^www\./, '').split('.')[0]}
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); handleRefresh(d); }}
                  disabled={loadingKeywords}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🔄 刷新
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(d); }}
                  style={{
                    padding: '4px 10px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
            <h4 style={{ margin: '0', fontSize: '14px', color: '#666' }}>
              {selectedDomain ? `${selectedDomain.domain} 的关键词` : '选择竞品查看关键词'}
            </h4>
            {selectedDomain && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
                >
                  <option value="volume">按搜索量</option>
                  <option value="etv">按预估流量</option>
                  <option value="rank">按排名</option>
                  <option value="difficulty">按难度</option>
                </select>
                <button
                  onClick={handleExport}
                  disabled={loadingKeywords || keywords.length === 0}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: loadingKeywords || keywords.length === 0 ? '#bdbdbd' : '#28a745',
                    color: '#fff',
                    cursor: loadingKeywords || keywords.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  导出 CSV
                </button>
              </div>
            )}
          </div>

          {loadingKeywords ? (
            <p style={{ color: '#999' }}>加载中...</p>
          ) : selectedDomain && keywords.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ textAlign: 'left', padding: '8px 4px', color: '#333' }}>关键词</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', color: '#333' }}>排名</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', color: '#333' }}>搜索量</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', color: '#333' }}>难度</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', color: '#333' }}>CPC($)</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', color: '#333' }}>预估流量</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px', color: '#333' }}>排名页面</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map(k => (
                  <tr key={k.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '8px 4px', color: '#333' }}>{k.keyword}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center', color: '#0084ff', fontWeight: '500' }}>{k.rank || '-'}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center', color: '#333' }}>{formatNumber(k.search_volume)}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        backgroundColor: k.difficulty >= 60 ? '#fdecea' : k.difficulty >= 30 ? '#fff3e0' : '#e8f5e9',
                        color: k.difficulty >= 60 ? '#c62828' : k.difficulty >= 30 ? '#e65100' : '#2e7d32'
                      }}>
                        {k.difficulty || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'center', color: '#333' }}>{k.cpc ? k.cpc.toFixed(2) : '-'}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center', color: '#333' }}>{formatNumber(k.etv)}</td>
                    <td style={{ padding: '8px 4px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {k.url ? (
                        <a href={k.url} target="_blank" rel="noreferrer" style={{ color: '#0084ff', textDecoration: 'none' }}>
                          {k.url.replace(/^https?:\/\//, '').substring(0, 40)}
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : selectedDomain ? (
            <p style={{ color: '#999' }}>该竞品暂无关键词数据，请点击「刷新」抓取</p>
          ) : (
            <p style={{ color: '#999' }}>请先添加竞品域名</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompetitorTracker;
